import { inject, Injectable } from '@angular/core';
import { Company } from '@lx/shared/types';
import { DOCUMENT } from '@angular/common';
import { getCompany } from '@lx/shared/tokens';
import { LoggerService } from '@lx/shared/logger';
import { BRANDS_WITH_DESIGN_TOKENS } from '../brand-theme.config';

/**
 * Service responsible for loading company-specific assets (theme CSS and favicon).
 * Company resolution and persistence to sessionStorage is handled at bootstrap time
 * via main.ts using resolveCompany() and saveCompanyToStorage() functions.
 * This service manages theme and favicon loading for the resolved company.
 *
 * Design tokens configuration is centralized in brand-theme.config.ts
 * to ensure consistency across all apps (index-transform.ts and this service).
 */
@Injectable({
  providedIn: 'root',
})
export class CompanyThemeLoaderService {
  private initialized = false;

  private readonly themeLinkId = 'company-theme-stylesheet';
  private readonly tokensLinkId = 'company-tokens-stylesheet';
  private readonly faviconSvgId = 'company-favicon-svg';
  private readonly faviconIcoId = 'company-favicon-ico';

  private readonly company = getCompany();
  private readonly document = inject(DOCUMENT);
  private readonly logger = inject(LoggerService);

  /**
   * Initializes the company configuration by loading company-specific assets.
   * Returns a Promise that resolves when the theme CSS is fully loaded.
   * Must be called via APP_INITIALIZER to block application bootstrap until theme is ready.
   *
   * @returns Promise that resolves when theme CSS is loaded or on error
   */
  initialize(): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }

    const promises: Promise<void>[] = [];

    // Set data-company attribute on document element for brand-specific CSS selectors
    this.setCompanyAttribute(this.company);

    // Load design tokens first if this company uses them
    if (BRANDS_WITH_DESIGN_TOKENS.includes(this.company)) {
      promises.push(this.loadDesignTokens(this.company));
    }

    // Always load the theme CSS
    promises.push(this.loadTheme(this.company));

    // Load favicon (non-blocking)
    this.loadFavicon(this.company);

    this.initialized = true;

    // Wait for all stylesheets to load
    return Promise.all(promises).then(() => undefined);
  }

  /**
   * Sets the data-company attribute on the document root element.
   * This allows applications to apply brand-specific CSS overrides.
   *
   * @param company - The company name to set as the attribute value
   */
  private setCompanyAttribute(company: Company): void {
    this.document.documentElement.setAttribute('data-company', company);
  }

  /**
   * Dynamically loads the design system tokens CSS for companies using the new tokens package.
   * Reuses existing link element to prevent duplicates during HMR/re-initialization
   *
   * @param company - The company to load tokens for
   * @returns Promise that resolves when the stylesheet is loaded
   */
  private loadDesignTokens(company: Company): Promise<void> {
    const existingLink = this.document.getElementById(this.tokensLinkId) as HTMLLinkElement | null;
    const tokensHref = `${company}-tokens.css`;

    if (existingLink) {
      existingLink.href = tokensHref;
      return this.waitForStylesheetLoad(existingLink);
    }

    const link = this.createLinkElement(this.tokensLinkId, 'stylesheet', tokensHref, 'text/css');
    this.document.head.appendChild(link);
    return this.attachStylesheetEventHandlers(link, company, 'tokens');
  }

  /**
   * Dynamically loads the company-specific theme CSS and returns a Promise that resolves
   * when the stylesheet is fully loaded, or rejects on error.
   * Reuses existing link element to prevent duplicates during HMR/re-initialization
   *
   * @param company - The company to load the theme for
   * @returns Promise that resolves when the stylesheet is loaded
   */
  private loadTheme(company: Company): Promise<void> {
    const existingLink = this.document.getElementById(this.themeLinkId) as HTMLLinkElement | null;
    const styleHref = `${company}-theme.css`;

    if (existingLink) {
      existingLink.href = styleHref;
      return this.waitForStylesheetLoad(existingLink);
    }

    const link = this.createLinkElement(this.themeLinkId, 'stylesheet', styleHref, 'text/css');
    this.document.head.appendChild(link);
    return this.attachStylesheetEventHandlers(link, company, 'theme');
  }

  /**
   * Waits for an existing stylesheet link element to fully load.
   * Handles cases where the stylesheet has already been cached by the browser.
   *
   * @param linkElement - The link element to monitor
   * @returns Promise that resolves when the stylesheet is loaded
   */
  private waitForStylesheetLoad(linkElement: HTMLLinkElement): Promise<void> {
    // Check if the stylesheet is already loaded (cached)
    if (linkElement.sheet) {
      return Promise.resolve();
    }

    // Otherwise, wait for the onload event
    return this.attachStylesheetEventHandlers(linkElement, this.company, 'theme');
  }

  /**
   * Attaches onload and onerror event handlers to a stylesheet link element.
   * Resolves the returned Promise when the stylesheet loads or fails.
   *
   * @param linkElement - The link element to attach handlers to
   * @param company - The company name for logging purposes
   * @param type - The type of stylesheet being loaded ('theme' or 'tokens')
   * @returns Promise that resolves when the stylesheet loads or fails
   */
  private attachStylesheetEventHandlers(linkElement: HTMLLinkElement, company: Company, type: 'theme' | 'tokens' = 'theme'): Promise<void> {
    return new Promise<void>((resolve) => {
      linkElement.onload = () => {
        this.logger.debug(`Successfully loaded ${type} CSS for company: ${company}`);
        resolve();
      };

      linkElement.onerror = () => {
        // Warn and resolve instead of reject to allow app to continue even if theme fails
        this.logger.warn(`Failed to load ${type} CSS for company: ${company}`);
        resolve();
      };
    });
  }

  /**
   * Dynamically loads company-specific favicon files (SVG and ICO)
   * Updates existing favicon elements or creates new ones if they don't exist
   * Handles load errors gracefully with console warnings
   */
  private loadFavicon(company: Company): void {
    this.createOrUpdateFaviconLink(this.faviconSvgId, 'icon', `/${company}-favicon.svg`, 'image/svg+xml');
    this.createOrUpdateFaviconLink(this.faviconIcoId, 'icon', `/${company}-favicon.ico`, 'image/x-icon', 'any');
  }

  /**
   * Creates or updates a favicon link element and attaches error handling
   * @param elementId - ID for the link element
   * @param rel - The rel attribute value
   * @param href - The href for the favicon file
   * @param type - The MIME type of the favicon
   * @param sizes - Optional sizes attribute
   */
  private createOrUpdateFaviconLink(elementId: string, rel: string, href: string, type: string, sizes?: string): void {
    const existingLink = this.document.getElementById(elementId) as HTMLLinkElement | null;

    if (existingLink) {
      existingLink.href = href;
      return;
    }

    const link = this.createLinkElement(elementId, rel, href, type, sizes);
    this.document.head.appendChild(link);
  }

  /**
   * Creates a favicon link element with the specified attributes
   * @param elementId - ID for the link element
   * @param rel - The rel attribute value
   * @param href - The href for the favicon file
   * @param type - The MIME type of the favicon
   * @param sizes - Optional sizes attribute
   * @returns The created link element
   */
  private createLinkElement(elementId: string, rel: string, href: string, type: string, sizes?: string): HTMLLinkElement {
    const link = this.document.createElement('link');
    link.id = elementId;
    link.rel = rel;
    link.type = type;
    link.href = href;

    if (sizes) {
      link.setAttribute('sizes', sizes);
    }

    return link;
  }
}
